import { defineComponent, provide, computed, watchEffect, ref, toRefs } from 'vue';
import { isObject, isUndefined, intersection } from 'lodash-es';

import Checkbox from './checkbox';
import props from './checkbox-group-props';
import { CheckboxOptionObj, TdCheckboxProps, CheckboxGroupValue } from './type';
import { CheckboxGroupInjectionKey } from './constants';
import { useVModel, useTNodeJSX, usePrefixClass, useChildComponentSlots } from '@tdesign/shared-hooks';

export default defineComponent({
  name: 'TCheckboxGroup',
  props,
  setup(props) {
    /** 样式 */
    const COMPONENT_NAME = usePrefixClass('checkbox-group');
    const renderTNodeJSX = useTNodeJSX();

    const { isArray } = Array;
    const { value, modelValue, onChange: onChangeRef } = toRefs(props);
    const [rawValue, setRawValue] = useVModel(value, modelValue, props.defaultValue, onChangeRef);
    const innerValue = computed(() => (isArray(rawValue.value) ? rawValue.value : []));

    const optionList = ref<Array<CheckboxOptionObj>>([]);

    const intersectionLen = computed<number>(() => {
      const values = optionList.value.map((item) => item.value);
      const n = intersection(innerValue.value, values);
      return n.length;
    });

    /**
     * 计算是否所有选项都被选中。
     * 此函数不接受参数，但依赖于外部的 `optionList` 和 `innerValue` 变量。
     *
     * @returns {boolean} 如果所有符合条件的选项都被选中，则返回 `true`；否则返回 `false`。
     */
    const isCheckAll = computed<boolean>(() => {
      // 筛选出非禁用、非只读且不设置为“全选”的选项，并提取其值
      const optionItems = optionList.value
        .filter((item) => !item.disabled && !item.readonly && !item.checkAll)
        .map((t) => t.value);

      // 计算当前选中值与筛选后的选项值的交集
      const intersectionValues = intersection(optionItems, innerValue.value);

      // 判断交集的长度是否等于所有选项值的长度，以确定是否所有选项都被选中
      return intersectionValues.length === optionItems.length;
    });

    const indeterminate = computed<boolean>(
      () => !isCheckAll.value && intersectionLen.value < optionList.value.length && intersectionLen.value !== 0,
    );

    const maxExceeded = computed<boolean>(() => !isUndefined(props.max) && innerValue.value.length === props.max);

    watchEffect(() => {
      if (!props.options) return [];
      optionList.value = props.options.map((item) => {
        return isObject(item) ? item : { label: String(item), value: item };
      });
    });

    /**
     * 获取所有复选框的值。
     * 此函数遍历 `optionList` 中的项，忽略被标记为 `checkAll`、`disabled` 或 `readonly` 的项，
     * 并收集非这些状态的项的值到一个 Set 集合中。如果达到最大限制 `maxExceeded`，则停止遍历。
     *
     */
    const getAllCheckboxValue = () => {
      const checkAllVal = new Set<TdCheckboxProps['value']>();
      const uncheckAllVal = new Set<TdCheckboxProps['value']>();
      // 遍历选项列表，忽略特定状态的项，并收集有效值
      for (let i = 0, len = optionList.value.length; i < len; i++) {
        const item = optionList.value[i];

        // 如果项被标记为检查所有、禁用或只读，且未选中，则跳过当前循环迭代
        if (item.checkAll) continue;
        if (item.disabled) {
          if (!innerValue.value.includes(item.value)) continue;
          else uncheckAllVal.add(item.value); // 添加禁用状态项的值到集合中
        }
        if (item.readonly) {
          if (!innerValue.value.includes(item.value)) continue;
          else uncheckAllVal.add(item.value); // 添加禁用状态项的值到集合中
        }

        checkAllVal.add(item.value); // 添加非排除状态项的值到集合中

        // 如果已达到最大限制，则终止循环
        if (maxExceeded.value) break;
      }

      return { checkAllVal: [...checkAllVal], uncheckAllVal: [...uncheckAllVal] }; // 从 Set 集合转换为数组并返回
    };

    const onCheckAllChange = (checked: boolean, context: { e: Event; source?: 't-checkbox' }) => {
      const { checkAllVal, uncheckAllVal } = getAllCheckboxValue();

      const value: CheckboxGroupValue = checked ? checkAllVal : uncheckAllVal;
      setRawValue(value, {
        e: context.e,
        type: checked ? 'check' : 'uncheck',
        current: undefined,
        option: undefined,
      });
    };

    const handleCheckboxChange = (data: { checked: boolean; e: Event; option: TdCheckboxProps }) => {
      const currentValue = data.option.value;
      const val = [...innerValue.value];
      if (data.checked) {
        val.push(currentValue);
      } else {
        const i = val.indexOf(currentValue);
        val.splice(i, 1);
      }
      setRawValue(val, {
        e: data.e,
        current: data.option.value,
        option: data.option,
        type: data.checked ? 'check' : 'uncheck',
      });
    };

    const onCheckedChange = (p: { checked: boolean; checkAll: boolean; e: Event; option: TdCheckboxProps }) => {
      const { checked, checkAll, e } = p;
      if (checkAll) {
        onCheckAllChange(checked, { e });
      } else {
        handleCheckboxChange(p);
      }
    };

    const getChildComponentSlots = useChildComponentSlots();

    const getOptionListBySlots = () => {
      const nodes = getChildComponentSlots('Checkbox');
      const arr: Array<CheckboxOptionObj> = [];
      nodes?.forEach((node) => {
        const option = node.props as CheckboxOptionObj;
        if (!option) return;
        // @ts-ignore types only declare checkAll not declare check-all
        if (option['check-all'] === '' || option['check-all'] === true) {
          option.checkAll = true;
        }
        arr.push(option);
      });
      return arr;
    };

    provide(
      CheckboxGroupInjectionKey,
      computed(() => ({
        name: props.name,
        isCheckAll: isCheckAll.value,
        checkedValues: innerValue.value,
        maxExceeded: maxExceeded.value,
        disabled: props.disabled,
        readonly: props.readonly,
        indeterminate: indeterminate.value,
        handleCheckboxChange,
        onCheckedChange,
      })),
    );

    return () => {
      let children = null;
      if (props.options?.length) {
        children = optionList.value?.map((option, index) => (
          <Checkbox
            key={`${option.value || ''}${index}`}
            lazyLoad={props.lazyLoad}
            {...option}
            index={index}
            checked={innerValue.value.includes(option.value)}
            data={option}
          ></Checkbox>
        ));
      } else {
        const nodes = renderTNodeJSX('default');
        optionList.value = getOptionListBySlots();
        children = nodes;
      }
      return (
        <div class={COMPONENT_NAME.value} role="group" aria-label="checkbox-group">
          {children}
        </div>
      );
    };
  },
});
